/**
 * Socle commun des graphiques maison.
 *
 * Aucune librairie de graphiques : c'est délibéré. Un dashboard d'exploitation
 * a besoin de six formes, pas d'un moteur générique de 400 ko qu'il faudrait
 * ensuite dompter pour lui faire respecter la charte.
 *
 * Trois règles tenues par TOUS les graphiques de ce dossier :
 *  1. `viewBox` + `width:100%` → responsive sans mesurer le DOM ;
 *  2. les couleurs viennent des variables CSS (`var(--c-blue)`), jamais d'un
 *     hexadécimal en dur → une retouche de palette se fait en un seul endroit ;
 *  3. `role="img"` + `aria-label` décrivant la TENDANCE en français → un lecteur
 *     d'écran obtient l'information, pas la liste des coordonnées.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

// ─── Données ────────────────────────────────────────────────────────────────

export interface SeriesPoint {
  /** Horodatage ISO du seau. */
  t: string;
  v: number;
}

export interface Series {
  key: string;
  label: string;
  /** Expression CSS (`var(--c-blue)`), pas un hexadécimal. */
  color: string;
  points: SeriesPoint[];
}

export interface Categorical {
  key: string;
  label: string;
  value: number;
  color: string;
}

// ─── Géométrie ──────────────────────────────────────────────────────────────

/** Largeur de repli, utilisée au tout premier rendu avant la mesure. */
export const VB_WIDTH = 720;

/**
 * Largeur réelle du conteneur, en pixels CSS.
 *
 * Pourquoi mesurer plutôt que fixer un `viewBox` : un `viewBox` de 720 affiché
 * dans une carte de 320 px met tout à l'échelle 0,44 — les étiquettes d'axe
 * tombent à 4 px et deviennent illisibles. En calant le `viewBox` sur la
 * largeur mesurée, une unité SVG vaut exactement un pixel CSS : le texte garde
 * la taille qu'on lui a donnée, quelle que soit la colonne où la carte atterrit.
 * `ResizeObserver` est une API native — aucune dépendance ajoutée.
 */
export function useMeasuredWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(VB_WIDTH);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0) setWidth(Math.round(rect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export interface Pad {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const DEFAULT_PAD: Pad = { top: 12, right: 14, bottom: 26, left: 46 };

// ─── Échelles ───────────────────────────────────────────────────────────────

/**
 * Graduations « rondes ». Un axe qui affiche 0 / 173 / 346 / 519 se lit deux
 * fois plus lentement qu'un axe 0 / 100 / 200 / 300 / 400.
 */
export function niceTicks(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const rawStep = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
  // La dernière graduation doit COUVRIR le maximum. Un axe qui s'arrête sous la
  // valeur la plus haute fait sortir la courbe (ou un repère) du cadre : le
  // dessin déborde alors sur le texte de la carte.
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step * 0.001; v += step) ticks.push(Number(v.toFixed(10)));
  return ticks.length >= 2 ? ticks : [0, step];
}

/** Abrège pour un axe : `1240` → `1,2 k`. */
export function formatAxisValue(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace('.', ',')} M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace('.', ',')} k`;
  if (!Number.isInteger(v)) return v.toFixed(1).replace('.', ',');
  return String(v);
}

/**
 * Aligne plusieurs séries sur un axe temporel commun.
 * Une série qui n'a pas de valeur pour un seau reçoit `null` — et non 0 :
 * « pas de mesure » et « zéro utilisateur » ne se dessinent pas pareil.
 */
export function alignSeries(series: Series[]): { ts: string[]; values: (number | null)[][] } {
  const set = new Set<string>();
  for (const s of series) for (const p of s.points) set.add(p.t);
  const ts = [...set].sort();
  const index = new Map(ts.map((t, i) => [t, i]));
  const values = series.map((s) => {
    const row: (number | null)[] = new Array<number | null>(ts.length).fill(null);
    for (const p of s.points) {
      const i = index.get(p.t);
      if (i !== undefined) row[i] = p.v;
    }
    return row;
  });
  return { ts, values };
}

export function seriesMax(values: (number | null)[][]): number {
  let max = 0;
  for (const row of values) for (const v of row) if (v !== null && v > max) max = v;
  return max;
}

export function stackedMax(values: (number | null)[][]): number {
  if (values.length === 0) return 0;
  const len = values[0]?.length ?? 0;
  let max = 0;
  for (let i = 0; i < len; i += 1) {
    let sum = 0;
    for (const row of values) sum += row[i] ?? 0;
    if (sum > max) max = sum;
  }
  return max;
}

// ─── Description accessible ─────────────────────────────────────────────────

function trendWord(first: number, last: number): string {
  if (first === last) return 'stable';
  const delta = last - first;
  if (first === 0) return delta > 0 ? 'en hausse' : 'en baisse';
  const pct = Math.round((Math.abs(delta) / first) * 100);
  return `${delta > 0 ? 'en hausse' : 'en baisse'} de ${pct} %`;
}

/**
 * Phrase française décrivant l'allure d'une ou plusieurs séries.
 * C'est ce que lira un lecteur d'écran : elle doit contenir l'information
 * qu'un voyant tire du dessin, pas la liste des points.
 */
export function describeSeries(title: string, series: Series[]): string {
  const parts: string[] = [];
  for (const s of series) {
    const vals = s.points.map((p) => p.v).filter((v) => Number.isFinite(v));
    if (vals.length === 0) {
      parts.push(`${s.label} : aucune donnée`);
      continue;
    }
    const first = vals[0] ?? 0;
    const last = vals[vals.length - 1] ?? 0;
    const max = Math.max(...vals);
    parts.push(
      `${s.label} : de ${formatAxisValue(first)} à ${formatAxisValue(last)}, maximum ${formatAxisValue(max)}, ${trendWord(first, last)}`,
    );
  }
  return parts.length === 0 ? `${title} : aucune donnée` : `${title}. ${parts.join('. ')}.`;
}

export function describeCategorical(title: string, rows: Categorical[]): string {
  if (rows.length === 0) return `${title} : aucune donnée`;
  const total = rows.reduce((acc, r) => acc + r.value, 0);
  const top = rows[0];
  const share = top && total > 0 ? Math.round((top.value / total) * 100) : 0;
  const head = top ? `${top.label} en tête avec ${formatAxisValue(top.value)} (${share} %)` : '';
  return `${title}. ${rows.length} catégories, total ${formatAxisValue(total)}. ${head}.`;
}

// ─── Cadre commun ───────────────────────────────────────────────────────────

export interface FrameProps {
  ariaLabel: string;
  /** Rendu du SVG quand il y a des données. */
  children: ReactNode;
  /** Quand `true`, le cadre affiche l'état vide explicite à la place du SVG. */
  empty: boolean;
  emptyLabel?: string;
  legend?: ReactNode;
  tooltip?: ReactNode;
  onPointerMove?: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerLeave?: () => void;
  /** Reçoit la `<figure>` pour que le graphique puisse se mesurer. */
  frameRef?: RefObject<HTMLElement | null>;
}

/**
 * Enveloppe partagée : c'est elle qui garantit qu'aucun graphique ne peut
 * afficher un cadre blanc muet quand la période ne contient rien.
 */
export function ChartFrame(props: FrameProps): React.ReactElement {
  const { ariaLabel, children, empty, emptyLabel, legend, tooltip, onPointerMove, onPointerLeave, frameRef } =
    props;
  if (empty) {
    return (
      <div className="state state-empty" role="img" aria-label={`${ariaLabel} : aucune donnée`}>
        <span className="state-title">Aucune donnée</span>
        <span>{emptyLabel ?? 'Aucune donnée sur la période sélectionnée.'}</span>
      </div>
    );
  }
  return (
    <figure className="chart" ref={frameRef} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}>
      {children}
      {tooltip}
      {legend ? <figcaption className="chart-legend">{legend}</figcaption> : null}
    </figure>
  );
}

export function Legend({ items }: { items: { label: string; color: string }[] }): React.ReactElement {
  return (
    <>
      {items.map((it) => (
        <span key={it.label}>
          <i className="dot-line" style={{ background: it.color }} aria-hidden="true" />
          {it.label}
        </span>
      ))}
    </>
  );
}

// ─── Survol ─────────────────────────────────────────────────────────────────

export interface HoverState {
  index: number;
  /** Position en pourcentage de la largeur / hauteur du conteneur. */
  leftPct: number;
  topPct: number;
}

/**
 * Survol d'un graphique à axe temporel.
 *
 * On convertit la position de la souris en fraction de la largeur du conteneur,
 * puis en index de seau. Aucune mesure de police ni de DOM interne n'est
 * nécessaire : le `viewBox` fait toute la mise à l'échelle.
 */
export function useTimeHover(
  count: number,
  vbWidth: number,
  pad: Pad = DEFAULT_PAD,
): { hover: HoverState | null; onPointerMove: (e: React.PointerEvent<HTMLElement>) => void; onPointerLeave: () => void } {
  const [hover, setHover] = useState<HoverState | null>(null);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (count <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      if (rect.width === 0) return;
      const vbX = ((e.clientX - rect.left) / rect.width) * vbWidth;
      const plotW = vbWidth - pad.left - pad.right;
      const step = count > 1 ? plotW / (count - 1) : 0;
      const raw = step === 0 ? 0 : (vbX - pad.left) / step;
      const index = Math.max(0, Math.min(count - 1, Math.round(raw)));
      const x = pad.left + index * step;
      setHover({ index, leftPct: (x / vbWidth) * 100, topPct: 0 });
    },
    [count, vbWidth, pad.left, pad.right],
  );

  const onPointerLeave = useCallback(() => setHover(null), []);
  return { hover, onPointerMove, onPointerLeave };
}

/** Construit un chemin `d` en sautant les trous (`null`). */
export function buildPath(
  row: (number | null)[],
  xOf: (i: number) => number,
  yOf: (v: number) => number,
): string {
  let d = '';
  let penDown = false;
  row.forEach((v, i) => {
    if (v === null) {
      penDown = false;
      return;
    }
    d += `${penDown ? 'L' : 'M'}${xOf(i).toFixed(2)},${yOf(v).toFixed(2)} `;
    penDown = true;
  });
  return d.trim();
}

// ─── Hachures ───────────────────────────────────────────────────────────────

/*
 * « Rien ici » a sa texture propre (passe bento, 2026-08-24).
 *
 * Pourquoi une hachure et pas un gris : un aplat gris EST une valeur — l'œil le
 * compare aux barres colorées et cherche ce qu'il représente. Une hachure n'est
 * comparable à rien : elle dit « il n'y a rien ici », ce qui est exactement le
 * message pour une part restante, une étape non atteinte, une piste vide.
 *
 * Usage — poser `<HatchDefs />` en premier enfant du `<svg>`, puis remplir avec
 * `HATCH_FILL`. L'identifiant est le même dans tous les graphiques : plusieurs
 * `<defs>` portant le même id coexistent sans conflit dans un document.
 *
 * Où l'appliquer, graphique par graphique :
 *  · BarChart  — la piste derrière chaque barre (la part non atteinte) ;
 *  · Funnel    — la piste derrière chaque étape (la part perdue) ;
 *  · Donut     — un secteur « autres / inconnu », jamais un secteur nommé ;
 *  · AreaStack — rien : une aire hachurée sous une autre devient illisible.
 *
 * Le motif est le jumeau SVG de `--hatch` dans `styles.css` : les jauges HTML
 * et les graphiques doivent porter exactement la même texture.
 */

/** Remplissage à passer en `fill`. */
export const HATCH_FILL = 'url(#chart-hatch)';

/** Motif de hachures, à inclure dans chaque `<svg>` qui l'utilise. */
export function HatchDefs(): React.ReactElement {
  return (
    <defs>
      <pattern id="chart-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
        {/* Un trait de 1,5 px tous les 7 px : assez dense pour se lire comme une
            texture à deux mètres, assez clair pour ne pas peser comme un aplat. */}
        <line x1="0" y1="0" x2="0" y2="7" stroke="var(--c-line)" strokeWidth={1.5} />
      </pattern>
    </defs>
  );
}
